import type { ReactNode } from 'react'
import { useDoc } from '@/data/hooks'
import type { BrandsSection, LocationsSection } from '@sonder/shared'
import { Button, Eyebrow, Icons } from '@/ui'

export function useBrands(clientId: string) {
  const { data } = useDoc<BrandsSection>(`clients/${clientId}/sections/brands`)
  const order = data?.order ?? []
  return { order, items: data?.items ?? {}, name: (id: string) => data?.items[id]?.name.value || 'Untitled brand' }
}
export function useLocations(clientId: string) {
  const { data, loading } = useDoc<LocationsSection>(`clients/${clientId}/sections/locations`)
  const order = data?.order ?? []
  return { order, items: data?.items ?? {}, loading, name: (id: string) => data?.items[id]?.name.value || 'Untitled location' }
}

/**
 * The heading of a block for one brand, location or group: grey eyebrow, large title. It sticks under the top bar
 * while its block scrolls, then the next block's heading takes over, so it's always clear whose fields these are.
 */
export function BlockHead({ eyebrow, title, aside, children }: { eyebrow?: string; title: string; aside?: ReactNode; children?: ReactNode }) {
  return (
    <header className="sticky top-[var(--topbar-h,4rem)] z-10 -mt-3 flex flex-wrap items-baseline justify-between gap-3 bg-paper/95 py-2 backdrop-blur sm:py-3">
      <div className="grid min-w-0 max-w-form gap-1">
        {eyebrow && <Eyebrow tone="muted">{eyebrow}</Eyebrow>}
        <h2 className="text-h4 sm:text-h3">{keepDotWithName(title)}</h2>
        {children}
      </div>
      {aside}
    </header>
  )
}

/** "Saints + Council · Colony Square" wraps after the dot, never before it, so no line starts with "·". */
export function keepDotWithName(title: string) {
  return title.replace(/ · /g, '\u00a0· ')
}

/** Card for one entity (brand, location, account) inside a section. */
export function EntityCard({ eyebrow, title, onRemove, removeLabel = 'Remove', children }: { eyebrow?: string; title: string; onRemove?: () => void; removeLabel?: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="grid gap-6 border-t border-ink pt-6">
      <BlockHead
        eyebrow={eyebrow}
        title={title}
        aside={
          onRemove && (
            <Button variant="danger-link" size="sm" arrow={false} onClick={onRemove}>
              {removeLabel}
            </Button>
          )
        }
      />
      <div className="grid max-w-form gap-6">{children}</div>
    </section>
  )
}

/**
 * A block only the Sonder team sees. Tinted like a callout so it reads as set apart from the form the client fills in.
 * Callers render it only in prep mode.
 */
export function TeamOnlyCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="grid gap-6 border-l-2 border-ink bg-info-wash px-5 py-6 sm:px-8">
      <header className="grid gap-1">
        <Eyebrow tone="muted">Team only · Not shown to the client</Eyebrow>
        <h2 className="text-h3">{title}</h2>
      </header>
      <div className="grid max-w-form gap-6">{children}</div>
    </section>
  )
}

export function AddRow({ label, onAdd, children }: { label: string; onAdd: () => void; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-3 border-t border-gray-200 pt-6">
      {children}
      <Button variant="ghost" size="sm" arrow={false} icon={<Icons.Plus size={12} />} onClick={onAdd} className="whitespace-normal py-2 text-left">
        {label}
      </Button>
    </div>
  )
}

export function NoEntities({ what, hint }: { what: string; hint: string }) {
  return (
    <p className="border border-dashed border-gray-300 px-6 py-8 text-small text-dim">
      No {what} yet. {hint}
    </p>
  )
}
